import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Popover,
    Typography,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    FormControl,
    Tooltip,
    IconButton,
    Divider,
    Slider,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import './TransitionHandle.styles.scss';

export type TransitionType = 'fade' | 'crossfade';
export type VisualTransitionType = 'none' | 'crossfade' | 'wipe' | 'fade' | 'blur';
export type WipeDirection = 'up' | 'down' | 'left' | 'right';
export type TransitionSide = 'in' | 'out';

export interface TransitionConfig {
    type: TransitionType;
    durationSec: number;
}

export interface VisualTransitionConfig {
    type: VisualTransitionType;
    durationSec: number;
    /** For wipe transitions: direction of the wipe */
    wipeDirection?: WipeDirection;
    /** For blur transitions: blur amount (1-1000) */
    blurAmount?: number;
}

interface TransitionHandleProps {
    side: TransitionSide; // 'in' = left/start, 'out' = right/end
    currentConfig?: TransitionConfig;
    hasAdjacentClip?: boolean; // True if there's a contiguous clip for crossfade option
    onConfigChange: (config: TransitionConfig) => void;
    disabled?: boolean;
    /** Offset from edge as percentage (0-100). Moves the handle inward based on fade duration. */
    offsetPercent?: number;
    /** Show visual transition options (for video/image clips) */
    showVisual?: boolean;
    /** Current visual transition config */
    visualConfig?: VisualTransitionConfig;
    /** Callback when visual config changes */
    onVisualConfigChange?: (config: VisualTransitionConfig) => void;
}

export const TransitionHandle: React.FC<TransitionHandleProps> = observer(({
    side,
    currentConfig,
    hasAdjacentClip = false,
    onConfigChange,
    disabled = false,
    offsetPercent = 0,
    showVisual = false,
    visualConfig,
    onVisualConfigChange,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [localDuration, setLocalDuration] = useState<string>(
        currentConfig?.durationSec?.toString() ?? '0'
    );
    const [localVisualDuration, setLocalVisualDuration] = useState<string>(
        visualConfig?.durationSec?.toString() ?? '0'
    );
    const handleRef = useRef<HTMLDivElement>(null);
    
    const isOpen = Boolean(anchorEl);
    
    // Calculate inline style for positioning based on offset
    const positionStyle: React.CSSProperties = {};
    if (offsetPercent > 0) {
        if (side === 'in') {
            positionStyle.left = `calc(${offsetPercent}% - 8px)`;  // -8px to center the 16px handle
        } else {
            positionStyle.right = `calc(${offsetPercent}% - 8px)`;
        }
    }
    
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (!disabled) {
            setAnchorEl(e.currentTarget);
            setLocalDuration(currentConfig?.durationSec?.toString() ?? '0');
            setLocalVisualDuration(visualConfig?.durationSec?.toString() ?? '0');
        }
    };
    
    const handleClose = () => {
        setAnchorEl(null);
    };
    
    // Audio handlers
    const handleTypeChange = (newType: TransitionType) => {
        onConfigChange({
            type: newType,
            durationSec: currentConfig?.durationSec ?? 0,
        });
    };
    
    const handleDurationBlur = () => {
        const parsed = parseFloat(localDuration);
        if (!isNaN(parsed) && parsed >= 0) {
            onConfigChange({
                type: currentConfig?.type ?? 'fade',
                durationSec: parsed,
            });
        } else {
            setLocalDuration(currentConfig?.durationSec?.toString() ?? '0');
        }
    };
    
    const handleDurationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleDurationBlur();
            (e.target as HTMLInputElement).blur();
        }
    };
    
    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
            setLocalDuration(value);
        }
    };
    
    // Visual handlers
    const handleVisualTypeChange = (newType: VisualTransitionType) => {
        onVisualConfigChange?.({
            ...visualConfig,
            type: newType,
            durationSec: visualConfig?.durationSec ?? (newType !== 'none' ? 0.5 : 0),
            wipeDirection: newType === 'wipe' ? (visualConfig?.wipeDirection ?? 'left') : undefined,
            blurAmount: newType === 'blur' ? (visualConfig?.blurAmount ?? 50) : undefined,
        });
    };
    
    const handleVisualDurationBlur = () => {
        const parsed = parseFloat(localVisualDuration);
        if (!isNaN(parsed) && parsed >= 0) {
            onVisualConfigChange?.({
                ...visualConfig,
                type: visualConfig?.type ?? 'none',
                durationSec: parsed,
            });
        } else {
            setLocalVisualDuration(visualConfig?.durationSec?.toString() ?? '0');
        }
    };
    
    const handleVisualDurationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleVisualDurationBlur();
            (e.target as HTMLInputElement).blur();
        }
    };
    
    const handleVisualDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
            setLocalVisualDuration(value);
        }
    };
    
    const handleWipeDirectionChange = (
        _event: React.MouseEvent<HTMLElement>,
        newDirection: WipeDirection | null
    ) => {
        if (newDirection) {
            onVisualConfigChange?.({
                ...visualConfig,
                type: 'wipe',
                durationSec: visualConfig?.durationSec ?? 0.5,
                wipeDirection: newDirection,
            });
        }
    };
    
    const handleBlurAmountChange = (_event: Event, value: number | number[]) => {
        const amount = Array.isArray(value) ? value[0] : value;
        onVisualConfigChange?.({
            ...visualConfig,
            type: 'blur',
            durationSec: visualConfig?.durationSec ?? 0.5,
            blurAmount: amount,
        });
    };
    
    // Clear/delete handlers
    const handleClearAudio = () => {
        onConfigChange({ type: 'fade', durationSec: 0 });
    };
    
    const handleClearVisual = () => {
        onVisualConfigChange?.({ type: 'none', durationSec: 0 });
    };
    
    const label = side === 'in' ? 'IN' : 'OUT';
    const hasTransition = currentConfig && currentConfig.durationSec > 0;
    const hasVisualTransition = visualConfig && visualConfig.type !== 'none' && visualConfig.durationSec > 0;
    
    return (
        <>
            <div
                ref={handleRef}
                className={`transition-handle transition-handle--${side} ${hasTransition ? 'transition-handle--active' : ''}`}
                style={positionStyle}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={handleClick}
                title={`${side === 'in' ? 'Fade In' : 'Fade Out'}`}
            >
                <div className="transition-handle__dot" />
            </div>
            
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: side === 'in' ? 'left' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: side === 'in' ? 'left' : 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            minWidth: '180px',
                            p: 1.5,
                        }
                    }
                }}
            >
                <Box 
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Visual Transitions - shown first for video/image clips */}
                    {showVisual && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: '#374151',
                                        fontSize: '13px',
                                    }}
                                >
                                    Visual
                                </Typography>
                                {hasVisualTransition && (
                                    <IconButton 
                                        size="small" 
                                        onClick={handleClearVisual}
                                        sx={{ p: 0.25, color: '#9CA3AF', '&:hover': { color: '#EF4444' } }}
                                        title="Remove transition"
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </Box>
                            
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={visualConfig?.type ?? 'none'}
                                    onChange={(e) => handleVisualTypeChange(e.target.value as VisualTransitionType)}
                                    sx={{
                                        fontSize: '13px',
                                        '& .MuiSelect-select': {
                                            py: 0.75,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        },
                                    }}
                                    startAdornment={
                                        <Typography 
                                            sx={{ 
                                                fontSize: '10px', 
                                                fontWeight: 600, 
                                                color: '#9CA3AF',
                                                mr: 0.5,
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    }
                                >
                                    <MenuItem value="none" sx={{ fontSize: '13px' }}>
                                        None
                                    </MenuItem>
                                    <MenuItem value="crossfade" sx={{ fontSize: '13px' }}>
                                        Crossfade
                                    </MenuItem>
                                    {/*<MenuItem value="wipe" sx={{ fontSize: '13px' }}>*/}
                                    {/*    Wipe*/}
                                    {/*</MenuItem>*/}
                                    <MenuItem value="fade" sx={{ fontSize: '13px' }}>
                                        Fade (to/from black)
                                    </MenuItem>
                                    {/*<MenuItem value="blur" sx={{ fontSize: '13px' }}>*/}
                                    {/*    Blur*/}
                                    {/*</MenuItem>*/}
                                </Select>
                            </FormControl>
                            
                            {/* Visual Duration - show for all types except 'none' */}
                            {visualConfig?.type && visualConfig.type !== 'none' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '12px', 
                                            color: '#6B7280',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Duration
                                    </Typography>
                                    <TextField
                                        size="small"
                                        value={localVisualDuration}
                                        onChange={handleVisualDurationChange}
                                        onBlur={handleVisualDurationBlur}
                                        onKeyDown={handleVisualDurationKeyDown}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                                                        s
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                fontSize: '13px',
                                                '& input': {
                                                    py: 0.5,
                                                    px: 1,
                                                    textAlign: 'right',
                                                },
                                            },
                                        }}
                                        sx={{
                                            width: '80px',
                                        }}
                                    />
                                </Box>
                            )}
                            
                            {/* Wipe Direction - only for wipe type */}
                            {visualConfig?.type === 'wipe' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '12px', 
                                            color: '#6B7280',
                                        }}
                                    >
                                        Direction
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={visualConfig?.wipeDirection ?? 'left'}
                                        exclusive
                                        onChange={handleWipeDirectionChange}
                                        size="small"
                                        sx={{ 
                                            '& .MuiToggleButton-root': { 
                                                py: 0.5, 
                                                px: 1,
                                                border: '1px solid #E5E7EB',
                                            },
                                            '& .MuiToggleButton-root.Mui-selected': {
                                                backgroundColor: '#3B82F6',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: '#2563EB' },
                                            },
                                        }}
                                    >
                                        <ToggleButton value="left" title="Left">
                                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                                        </ToggleButton>
                                        <ToggleButton value="right" title="Right">
                                            <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                        </ToggleButton>
                                        <ToggleButton value="up" title="Up">
                                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                        </ToggleButton>
                                        <ToggleButton value="down" title="Down">
                                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                            
                            {/* Blur Amount - only for blur type */}
                            {visualConfig?.type === 'blur' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography 
                                            sx={{ 
                                                fontSize: '12px', 
                                                color: '#6B7280',
                                            }}
                                        >
                                            Blur Amount
                                        </Typography>
                                        <Typography 
                                            sx={{ 
                                                fontSize: '12px', 
                                                color: '#374151',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {visualConfig?.blurAmount ?? 50}
                                        </Typography>
                                    </Box>
                                    <Slider
                                        value={visualConfig?.blurAmount ?? 50}
                                        onChange={handleBlurAmountChange}
                                        min={1}
                                        max={1000}
                                        size="small"
                                        sx={{
                                            color: '#3B82F6',
                                            '& .MuiSlider-thumb': {
                                                width: 14,
                                                height: 14,
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                            
                            <Divider sx={{ my: 0.5 }} />
                        </>
                    )}
                    
                    {/* Audio Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 600, 
                                color: '#374151',
                                fontSize: '13px',
                            }}
                        >
                            Audio
                        </Typography>
                        {hasTransition && (
                            <IconButton 
                                size="small" 
                                onClick={handleClearAudio}
                                sx={{ p: 0.25, color: '#9CA3AF', '&:hover': { color: '#EF4444' } }}
                                title="Remove transition"
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                    
                    {/* Audio Type Selector */}
                    <FormControl size="small" fullWidth>
                        <Select
                            value={currentConfig?.type ?? 'fade'}
                            onChange={(e) => handleTypeChange(e.target.value as TransitionType)}
                            sx={{
                                fontSize: '13px',
                                '& .MuiSelect-select': {
                                    py: 0.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                },
                            }}
                            startAdornment={
                                <Typography 
                                    sx={{ 
                                        fontSize: '10px', 
                                        fontWeight: 600, 
                                        color: '#9CA3AF',
                                        mr: 0.5,
                                    }}
                                >
                                    {label}
                                </Typography>
                            }
                        >
                            <MenuItem value="fade" sx={{ fontSize: '13px' }}>
                                Fade
                            </MenuItem>
                            <MenuItem 
                                value="crossfade" 
                                disabled={!hasAdjacentClip}
                                sx={{ 
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span>Crossfade</span>
                                {!hasAdjacentClip && (
                                    <Tooltip title="Crossfade requires an adjacent clip" arrow>
                                        <InfoOutlinedIcon sx={{ fontSize: 14, ml: 1, color: '#9CA3AF' }} />
                                    </Tooltip>
                                )}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    
                    {/* Audio Duration Input */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                            sx={{ 
                                fontSize: '12px', 
                                color: '#6B7280',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Duration
                        </Typography>
                        <TextField
                            size="small"
                            value={localDuration}
                            onChange={handleDurationChange}
                            onBlur={handleDurationBlur}
                            onKeyDown={handleDurationKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                                            s
                                        </Typography>
                                    </InputAdornment>
                                ),
                                sx: {
                                    fontSize: '13px',
                                    '& input': {
                                        py: 0.5,
                                        px: 1,
                                        textAlign: 'right',
                                    },
                                },
                            }}
                            sx={{
                                width: '80px',
                            }}
                        />
                    </Box>
                </Box>
            </Popover>
        </>
    );
});

export default TransitionHandle;

